import { JSX, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { NetworkIntegrityAggregateTable, NetworkIntegrityWidgetInfoFlyout, ProvidersBySpecialtyDescription, WidgetMenu } from ".";
import { NetworkIntegrityContext } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { NetworkIntegrityAggregateResults, NetworkIntegrityAggregateTableRow } from "@app/projects/network-assessment/network-integrity/models";
import { NetworkIntegrityActionStatus } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { NetworkIntegrityAggregateGroup, NetworkIntegrityJobStatus } from "@app/projects/enumerators";
import { getPendoClassNameForAggregateGroup } from "@app/projects/network-assessment/network-integrity/helpers/networkIntegrityPendoHelper";
import { shouldShowViewAllFooter } from "@app/projects/network-assessment/network-integrity/helpers/viewAllFooterHelper";
import { WidgetFooter, WidgetLoading, WidgetHeader, Widget, WidgetBody } from "./Widget";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";

const ProvidersBySpecialtyWidget = (): JSX.Element => {
  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const ffContext = useContext(FeatureFlagContext);
  const showTermedStatus = ffContext.enabledFeatureFlags.has(Features.DISPLAY_NETWORK_INTEGRITY_TOTAL_AFTER_TERMED);
  const project = networkIntegrityContext.project!;
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);

  const [providersBySpecialtyResults, setProvidersBySpecialtyResults] = useState<NetworkIntegrityAggregateResults>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const title = 'Providers By Specialty';

  useEffect(() => {
    let cancelled = false;
    setProvidersBySpecialtyResults(null);
    async function fetchProvidersBySpecialtyResults() {
      try {
        if (networkIntegrityContext.projectRunConfigurationId) {
          const result = await networkIntegrityService.getProvidersBySpecialtyResults(project.projectId, networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes);
          if (!cancelled) setProvidersBySpecialtyResults(result);
        }
      } finally {
        if (!cancelled) setLoading(false);
      };
    };

    if (networkIntegrityContext.isJobStatusLoading === false && networkIntegrityContext.jobStatus == NetworkIntegrityJobStatus.Finished) {
      setLoading(true);
      fetchProvidersBySpecialtyResults();
    }

    return () => { cancelled = true; };
  }, [networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.jobStatus, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes]);

  const showFooter = (): ReactNode => {
    return shouldShowViewAllFooter(showTermedStatus, providersBySpecialtyResults?.totalAggregateCount) &&
      <WidgetFooter>
        <Link className={getPendoClassNameForAggregateGroup(NetworkIntegrityAggregateGroup.ProvidersBySpecialty, "view-all")}
          to={{
            pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/all/${NetworkIntegrityAggregateGroup.ProvidersBySpecialty}`,
            state: {
              initialProviderType: networkIntegrityContext.providerType,
              initialRegionSetIds: networkIntegrityContext.regionSetIds,
              initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
              runConfiguration: networkIntegrityContext.projectRunConfiguration
            }
          }}>
            View All
        </Link>
      </WidgetFooter>
  }

  if (networkIntegrityContext.isLoading || loading || !networkIntegrityContext.jobStatus)
    return (<WidgetLoading>
      <WidgetHeader title={title} />
      </WidgetLoading>);

  const rows: NetworkIntegrityAggregateTableRow[] = providersBySpecialtyResults?.networkIntegrityAggregates.map((result, index) => {
    const unknownKey = "Unknown"
    const [key, specialtyName] = result.aggregateKey.split('|');
    const aggregateKey = key === unknownKey
      ? specialtyName
        ? `${unknownKey}: ${specialtyName}`
        : unknownKey
      : `${key} - ${specialtyName}`;

    const getProviderDetailsLink = (count: number, actionStatus?: NetworkIntegrityActionStatus) => {
      return (
        count != 0 ?
        <Link className={`${getPendoClassNameForAggregateGroup(NetworkIntegrityAggregateGroup.ProvidersBySpecialty, index.toString())}`}
          to={{
            pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/providers/${networkIntegrityContext.providerType}/${NetworkIntegrityAggregateGroup.ProvidersBySpecialty}/${encodeURIComponent(result.aggregateKey)}`,
            state: {
              initialActionStatus: actionStatus,
              initialRegionSetIds: networkIntegrityContext.regionSetIds,
              initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
              runConfiguration: networkIntegrityContext.projectRunConfiguration
            }
          }}>
          {count?.toLocaleString()}
        </Link> :
        <span>0</span>
      );
    };

    const count = result ? getProviderDetailsLink(result.count) : undefined;
    const afterTermedCount = result?.afterTermedCount != null ? getProviderDetailsLink(result.afterTermedCount, NetworkIntegrityActionStatus.NoActionsApplied) : undefined;

    return { afterTermedCount, aggregateKey, count, rawAfterTermedCount: result?.afterTermedCount ?? undefined, rawCount: result?.count };
  });

  return (
    <Widget>
      <WidgetHeader title={title}>
        <WidgetMenu>
          <NetworkIntegrityWidgetInfoFlyout title="Providers By Specialty" description={<ProvidersBySpecialtyDescription/>}/>
        </WidgetMenu>
      </WidgetHeader>
      <WidgetBody>
        <NetworkIntegrityAggregateTable aggregateHeader="Specialty" aggregateCountHeader="Unique Access Points" rows={rows} />
      </WidgetBody>
      {showFooter()}
    </Widget>);
}

export { ProvidersBySpecialtyWidget };